import React from "react";

import Banners from "../sections/Banners";
import EventsPreview from "../sections/EventsPreview";
import CaissesPreview from "../sections/CaissesPreview";
import HorairesPreview from "../sections/HorairesPreview";
import ArticlesPreview from "../sections/ArticlesPreview";
import GalleryPreview from "../sections/GalleryPreview";
import ContactBlock from "../sections/ContactBlock";
import DonationStrip from "../sections/DonationStrip";
import Newsletter from "../sections/Newsletter";
import CustomContent from "../sections/CustomContent";
import MainContent from "../sections/MainContent";
import ShabbatTimes from "../sections/ShabbatTimes";
import Zmanim from "../sections/Zmanim";
import { applyLang } from "../../../i18n";
import logger from "../../../services/logger"

/**
 * Renderer générique d'une section modulaire.
 * Le même composant est utilisé pour les sections homepage ET pour les
 * sections globales pré-footer — un type de section n'a pas conscience
 * de son scope, il fait juste son rendu.
 *
 * `section.hidden` → null.
 * `section.type` → dispatch vers le composant correspondant.
 * `section.props` → props additionnelles (ex. source de bannières, editorPageId).
 */
export default function SectionRenderer({ section, context }) {
    if (!section || section.hidden) return null;

    // Les props sont stockés avec leurs sous-objets multilangues (fr, en, he…).
    // applyLang merge la langue courante en surface pour que les composants
    // reçoivent directement les chaînes localisées.
    const props = applyLang(section.props || {}, context.lang);

    switch (section.type) {
        case "BANNERS":
            return <Banners context={context} source={props.source || "banners.items"} />;

        case "EVENTS_PREVIEW":
            return <EventsPreview context={context} {...props} />;

        case "CAISSES_PREVIEW":
            return <CaissesPreview context={context} {...props} />;

        case "HORAIRES_PREVIEW":
            return <HorairesPreview context={context} {...props} />;

        case "ARTICLES_PREVIEW":
            return <ArticlesPreview context={context} {...props} />;

        case "GALLERY_PREVIEW":
            return <GalleryPreview context={context} {...props} />;

        case "CONTACT_BLOCK":
            return <ContactBlock context={context} {...props} />;

        case "DONATION_STRIP":
            return <DonationStrip context={context} {...props} />;

        case "NEWSLETTER":
            return <Newsletter context={context} {...props} />;

        case "CUSTOM_CONTENT":
            // Pour les sections custom : on passe TOUS les props (contentType,
            // aiContentBlockId, editorPageId) + l'id de la section comme fallback
            // editorPageId (la route éditeur "section" utilise l'id de la section).
            return (
                <CustomContent
                    context={context}
                    editorPageId={props.editorPageId || section.id}
                    aiContentBlockId={props.aiContentBlockId}
                    contentType={props.contentType}
                />
            );

        case "MAIN_CONTENT":
            return <MainContent context={context} />;

        case "SHABBAT_TIMES":
            // Horaires du prochain Chabbat (calcul Hebcal côté front).
            // props: style (BANNER|CARD|TILES), eyebrow, title, subtitle,
            // ctaLabel, ctaUrl (CTA optionnel).
            return <ShabbatTimes context={context} {...props} />;

        case "ZMANIM":
            // Horaires halakhiques du jour (calcul Hebcal côté front).
            // props: eyebrow, title, subtitle, limit.
            return <Zmanim context={context} {...props} />;

        default:
            if (process?.env?.NODE_ENV !== "production") {
                logger.warn({ type: section.type }, `[2026] SectionRenderer: type inconnu "${section.type}"`);
            }
            return null;
    }
}
